import {
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { Operation, ScopeType } from "types";
import React, { useState } from "react";
import {
  greenRadioSx,
  labelDisabledSx,
  permissionsFormLabelSx,
  permissionsHeaderSx,
  redRadioSx,
} from "utils";
import { useTranslation } from "react-i18next";
import { EntityPermissionsProps } from "pages/permissions";

const ContactPermissions: React.FC<EntityPermissionsProps> = ({
  roleID,
  entityID,
  handleSubmit,
  initialState,
  mobile,
  readonly,
}) => {
  const { t } = useTranslation();
  const [read, setRead] = useState<ScopeType>(initialState.initialRead);
  const [create, setCreate] = useState<ScopeType>(initialState.initialCreate);
  const [update, setUpdate] = useState<ScopeType>(initialState.initialUpdate);

  const handleReadChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRead(event.target.value as ScopeType);
    if (event.target.value === ScopeType.None) {
      setCreate(ScopeType.None);
      setUpdate(ScopeType.None);
    }
    handleSubmit(roleID, entityID, event, Operation.READ);
  };

  const handleCreateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCreate(event.target.value as ScopeType);
    handleSubmit(roleID, entityID, event, Operation.CREATE);
  };

  const handleUpdateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUpdate(event.target.value as ScopeType);
    handleSubmit(roleID, entityID, event, Operation.UPDATE);
  };

  return (
    <Stack
      direction={"column"}
      spacing={mobile ? 2 : 3}
      sx={{ ...(readonly ? labelDisabledSx : {}), ...{ pb: 2 } }}
    >
      <Typography variant={"h3"} sx={permissionsHeaderSx}>
        {t("contact.title", { ns: "permissions" })}
      </Typography>
      <FormControl disabled={readonly}>
        <Stack direction={mobile ? "column" : "row"} display={"flex"}>
          <FormLabel id="contact-read-radio-group" sx={permissionsFormLabelSx}>
            <Typography
              variant={"subtitle1"}
              align={mobile ? "center" : "left"}
            >
              {t("read", { ns: "permissions" })}
            </Typography>
            {mobile && <Divider variant={"middle"} />}
          </FormLabel>
          <RadioGroup
            aria-labelledby="contact-read-radio-group"
            value={read}
            onChange={handleReadChange}
            sx={{ flex: 8 }}
          >
            <FormControlLabel
              value={ScopeType.None}
              control={<Radio sx={redRadioSx} />}
              label={
                <Typography variant={mobile ? "subtitle2" : "body1"}>
                  {t("contact.read_none", { ns: "permissions" })}
                </Typography>
              }
            />
            <FormControlLabel
              value={ScopeType.Role}
              control={<Radio sx={greenRadioSx} />}
              label={
                <Typography variant={mobile ? "subtitle2" : "body1"}>
                  {t(`contact.read_${ScopeType.Role}${roleID === 5 ? "_PM" : ""}`, {
                    ns: "permissions",
                  })}
                </Typography>
              }
            />
          </RadioGroup>
        </Stack>
      </FormControl>
      <FormControl disabled={read === ScopeType.None || readonly}>
        <Stack direction={mobile ? "column" : "row"} display={"flex"}>
          <FormLabel
            id="contact-update-radio-group"
            sx={permissionsFormLabelSx}
          >
            <Typography
              variant={"subtitle1"}
              align={mobile ? "center" : "left"}
            >
              {t("update", { ns: "permissions" })}
            </Typography>
            {mobile && <Divider variant={"middle"} />}
          </FormLabel>
          <RadioGroup
            aria-labelledby="contact-update-radio-group"
            value={update}
            onChange={handleUpdateChange}
            sx={{ flex: 8 }}
          >
            <FormControlLabel
              value={ScopeType.None}
              control={<Radio sx={redRadioSx} />}
              label={
                <Typography variant={mobile ? "subtitle2" : "body1"}>
                  {t("contact.update_none", { ns: "permissions" })}
                </Typography>
              }
            />
            <FormControlLabel
              value={ScopeType.Role}
              control={<Radio sx={greenRadioSx} />}
              label={
                <Typography variant={mobile ? "subtitle2" : "body1"}>
                  {t(`contact.update_${ScopeType.Role}${roleID === 5 ? "_PM" : ""}`, {
                    ns: "permissions",
                  })}
                </Typography>
              }
            />
          </RadioGroup>
        </Stack>
      </FormControl>
      <FormControl disabled={read === ScopeType.None || readonly}>
        <Stack direction={mobile ? "column" : "row"} display={"flex"}>
          <FormLabel
            id="contact-create-radio-group"
            sx={permissionsFormLabelSx}
          >
            <Typography
              variant={"subtitle1"}
              align={mobile ? "center" : "left"}
            >
              {t("create", { ns: "permissions" })}
            </Typography>
            {mobile && <Divider variant={"middle"} />}
          </FormLabel>
          <RadioGroup
            aria-labelledby="contact-create-radio-group"
            value={create}
            onChange={handleCreateChange}
            sx={{ flex: 8 }}
          >
            <FormControlLabel
              value={ScopeType.None}
              control={<Radio sx={redRadioSx} />}
              label={
                <Typography variant={mobile ? "subtitle2" : "body1"}>
                  {t("contact.create_none", { ns: "permissions" })}
                </Typography>
              }
            />
            <FormControlLabel
              value={ScopeType.Role}
              control={<Radio sx={greenRadioSx} />}
              label={
                <Typography variant={mobile ? "subtitle2" : "body1"}>
                  {t("contact.create_role", { ns: "permissions" })}
                </Typography>
              }
            />
          </RadioGroup>
        </Stack>
      </FormControl>
    </Stack>
  );
};

export default ContactPermissions;
